import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, ExternalLink, Loader2, Video, RefreshCw } from "lucide-react";

export default function LiveRoom() {
  const { classId } = useParams();
  const nav = useNavigate();
  const startedRef = useRef(false);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Loading class…");

  useEffect(() => {
    let mounted = true;
    api.get(`/classes/${classId}/join`)
      .then(({ data }) => { if (mounted) setMeta(data); })
      .catch((e) => setError(e.response?.data?.detail || "Cannot join class"));
    return () => { mounted = false; };
  }, [classId]);

  useEffect(() => {
    if (!meta || startedRef.current) return;
    if (meta.provider !== "zoom" || !meta.zoom_meeting_id) return;
    startedRef.current = true;
    (async () => {
      try {
        setStatus("Loading Zoom SDK…");
        let tries = 0;
        while (!window.ZoomMtg && tries < 60) {
          await new Promise((r) => setTimeout(r, 100));
          tries++;
        }
        const ZoomMtg = window.ZoomMtg;
        if (!ZoomMtg) throw new Error("Zoom SDK failed to load");
        ZoomMtg.setZoomJSLib("https://source.zoom.us/4.0.0/lib", "/av");
        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();

        setStatus("Authorizing session…");
        const role = meta.is_moderator ? 1 : 0;
        const meetingNumber = String(meta.zoom_meeting_id).replace(/\s+/g, "");
        const { data: sig } = await api.post("/zoom/sdk-signature", {
          meetingNumber, role,
        });

        const leaveUrl = `${window.location.origin}/dashboard`;
        setStatus("Initializing…");
        await new Promise((resolve, reject) => {
          ZoomMtg.init({
            leaveUrl, patchJsMedia: true,
            disableInvite: false, disableCallOut: true,
            screenShare: true, videoDrag: true,
            showMeetingHeader: true, isSupportAV: true,
            isSupportChat: true, isSupportQA: true,
            isSupportPolling: true, isSupportBreakout: true,
            isSupportCC: true, success: resolve, error: reject,
          });
        });

        setStatus("Joining meeting…");
        await new Promise((resolve, reject) => {
          ZoomMtg.join({
            signature: sig.signature, sdkKey: sig.sdk_key,
            meetingNumber, passWord: meta.zoom_password || "",
            userName: meta.user_name || "Student", userEmail: meta.user_email || "",
            tk: "", zak: "",
            success: resolve, error: reject,
          });
        });
        setStatus("");
      } catch (e) {
        console.error("Zoom join error", e);
        const msg = e?.errorMessage || e?.reason || e?.message || (typeof e === "string" ? e : "Zoom embed failed");
        setError(msg);
        setStatus("");
      }
    })();
  }, [meta]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-strong rounded-2xl p-8 max-w-md text-center" data-testid="live-room-error">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
          <div className="font-display text-xl font-semibold mt-3">Unable to start the class</div>
          <div className="text-sm text-[var(--text-dim)] mt-2 break-words">{String(error)}</div>
          {String(error).toLowerCase().includes("signature") && (
            <div className="text-xs text-amber-200 mt-3 bg-amber-500/10 p-3 rounded-lg text-left">
              <b>Fix needed:</b> Marketplace pe Meeting SDK app activate karein. Marketplace → Build App → "Meeting SDK" type → use uske SDK Key/Secret (NOT General OAuth Client ID/Secret) → backend .env me ZOOM_SDK_KEY / ZOOM_SDK_SECRET update karein → redeploy.
            </div>
          )}
          <div className="flex gap-2 justify-center mt-5 flex-wrap">
            <button onClick={() => { startedRef.current = false; setError(null); setStatus("Retrying…"); }} className="btn-primary"><RefreshCw className="w-4 h-4" /> Retry</button>
            <button onClick={() => nav(-1)} className="btn-ghost">Back</button>
          </div>
        </div>
      </div>
    );
  }
  if (!meta) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--text-dim)]"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connecting…</div>;
  }
  const isZoom = meta.provider === "zoom" && meta.zoom_meeting_id;
  if (!isZoom) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-strong rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-8 h-8 text-amber-400 mx-auto" />
          <div className="font-display text-xl font-semibold mt-3">Zoom meeting not configured</div>
          <div className="text-sm text-[var(--text-dim)] mt-2">Admin panel → class row → "Zoom" button click karke meeting create karein.</div>
          <button onClick={() => nav(-1)} className="btn-primary mt-5">Back</button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[var(--bg-deep)] flex items-center justify-center" data-testid="live-room">
      <div className="text-center px-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center mb-5 shadow-[0_0_60px_-10px_rgba(236,72,153,0.7)] mx-auto"><Video className="w-10 h-10 text-white" /></div>
        <div className="font-display text-2xl font-semibold flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> {status || "Launching classroom…"}</div>
        <div className="text-xs text-[var(--text-mute)] mt-3 max-w-sm mx-auto">{meta.class_title} • {meta.is_moderator ? "Host" : "Participant"}</div>
        <div className="text-[10px] text-[var(--text-mute)] mt-2">Camera & microphone permission allow karein.</div>
      </div>
    </div>
  );
}
